import { observable, action, runInAction, computed, makeObservable } from 'mobx';
import Member from '../models/member.model';
import IApp from '../models/app.model';
import IReferral from '../models/referral.model';

import ConnectionService, { ConnectionError } from '../services/connection/connection.service';
import commonStore from './common.store';
import IAccount, { IAuthKey, AuthKeyType, IWebhookIntegrationData, WebhookEventType } from '../models/account.model';
import trackAnalytic from '../components/analytics';
import IPlan from '../models/plan.model';
import loglyticsStore from './loglytics.store';
import IApiKeyCreate from '../models/dto/apikey_create.model';
import { ISubscriptionGroup } from '../models/subscription_group.model';
import AccountConnection from '../services/connection/account.connection';
import githubConnection from '../services/connection/github.connection';
import memberConnection from '../services/connection/member.connection';
import alertsStore from './alerts.store';
import { getLogger } from '../services/logger.service';

const log = getLogger('MemberStore');

export class MemberStore {
  member?: Member = undefined;
  referrals: IReferral[] = [];
  accountId?: string = undefined;
  appId?: string = undefined;
  loadingMember?: boolean = undefined;
  currentPlan?: IPlan = undefined;
  subscriptionGroups: ISubscriptionGroup[] = [];


  constructor() {
    makeObservable(this, {
      member: observable,
      referrals: observable,
      accountId: observable,
      appId: observable,
      loadingMember: observable,
      currentPlan: observable,
      subscriptionGroups: observable,
      account: computed,
      app: computed,
      pullMember: action,
      getSubscriptionGroups: action,
      suppressEmail: action,
      getCurrentPlan: action,
      addAccountAuthKey: action,
      deleteAccountAuthKey: action,
      updateAccountAuthKey: action,
      updateSlackIntegration: action,
      removeSlackIntegration: action,
      setGithubRepoMapping: action,
      removeGithubRepoMapping: action,
      disconnectGithub: action,
      setMember: action,
      forgetMember: action,
      updateMyMemberDetails: action,
      markAnnouncementSeen: action,
      updateAccount: action,
      updateApp: action,
      addApp: action,
      addAccount: action,
      deleteApp: action,
      setAppId: action,
      setAccountId: action
    });
  }


  get account() {
    return this.member?.accounts?.find(account => account._id === this.accountId);
  }

  appName(appId: string) {
    return this.account?.apps.find(app => app._id === appId)?.name ?? appId;
  }

  get app() {
    if (!this.account) return;
    let app = this.account.apps.find(app => app._id === this.appId);
    if (!app && this.account.apps.length > 0) {
      app = this.account.apps[0];
      this.setAppId(app._id);
    }
    return  app;
  }

  async pullMember() {
    this.loadingMember = true;
    commonStore.setLoading(true);
    try {
      const member = await memberConnection.getMember();
      this.setMember(member);
    } catch (err) {
      log.e('error pulling member', err);
      // Only a server rejection invalidates the session — a network failure
      // (aborted navigation, offline) must not log the user out.
      if (this.isAuthRejection(err)) commonStore.setToken(undefined);
      throw err;
    } finally {
      this.loadingMember = false;
      commonStore.setLoading(false);
    }
  }

  private isAuthRejection(err: unknown) {
    return err instanceof ConnectionError && (err.status === 401 || err.status === 403);
  }

  async getSubscriptionGroups() {
    const subscriptionGroups = await AccountConnection.getSubscriptionGroups();
    runInAction(() => this.subscriptionGroups = subscriptionGroups);
  }

  async suppressEmail(data: object) {
    const subscriptionGroups = await AccountConnection.suppressEmail(data);
    runInAction(() => this.subscriptionGroups = subscriptionGroups);
  }

  async getCurrentPlan() {
    this.loadingMember = true;
    try {
      const currentPlan = await AccountConnection.getCurrentPlan();
      runInAction(() => { this.currentPlan = currentPlan; });
    } catch (err) {
      log.e('error getting current plan', err);
      if (this.isAuthRejection(err)) commonStore.setToken(undefined);
      throw err;
    } finally {
      this.loadingMember = false;
    }
  }

  async addAccountAuthKey(name:string, type: AuthKeyType): Promise<IApiKeyCreate> {
    return await ConnectionService.addAccountAuthKey(name, type);
  }

  async deleteAccountAuthKey(keyToBeDeleted:IAuthKey) {
    await ConnectionService.deleteAccountAuthKey(keyToBeDeleted.key);
    return await this.pullMember();
  }

  async updateAccountAuthKey(keyToBeUpdated:IAuthKey, name:string): Promise<void> {
    return await ConnectionService.updateAccountAuthKey(keyToBeUpdated.key, name);
  }


  async updateSlackIntegration(integrationId:string, appId?: string): Promise<void> {
    await ConnectionService.updateSlackIntegration(integrationId, appId);
    return await this.pullMember();
  }
  async removeSlackIntegration(integrationId:string): Promise<void> {
    await ConnectionService.removeSlackIntegration(integrationId);
    return await this.pullMember();

  }

  async setGithubRepoMapping(appId: string, data: {owner: string, repo: string, branch: string}): Promise<void> {
    await githubConnection.setRepoMapping(appId, data);
    return await this.pullMember();
  }
  async removeGithubRepoMapping(appId: string): Promise<void> {
    await githubConnection.deleteRepoMapping(appId);
    return await this.pullMember();
  }
  async disconnectGithub(): Promise<void> {
    await githubConnection.disconnect();
    return await this.pullMember();
  }

  async addWebhookIntegration(data: IWebhookIntegrationData): Promise<string | undefined> {
    const integration = await ConnectionService.addWebhookIntegration(data);
    await this.pullMember();
    return integration.secret;
  }
  async regenerateWebhookSecret(integrationId: string): Promise<string> {
    const {secret} = await ConnectionService.regenerateWebhookSecret(integrationId);
    return secret;
  }
  async updateWebhookIntegration(integrationId: string, data: IWebhookIntegrationData): Promise<void> {
    await ConnectionService.updateWebhookIntegration(integrationId, data);
    return await this.pullMember();
  }
  async removeWebhookIntegration(integrationId: string): Promise<void> {
    await ConnectionService.removeWebhookIntegration(integrationId);
    return await this.pullMember();
  }
  async testWebhookIntegration(integrationId: string, type: WebhookEventType): Promise<void> {
    await ConnectionService.testWebhookIntegration(integrationId, type);
  }

  setMember(member: Member) {
    this.member = member;
    trackAnalytic.user(this.member);
    if (member.accounts?.length > 0) {
      this.accountId = member.accounts[0]._id;
      this.setAppId(member.accounts[0].apps.length > 0 ? member.accounts[0].apps[0]._id : undefined)
      log.d(`set member app:${this.app?.name} `)
      this.getCurrentPlan();
    }
    
    loglyticsStore.clear();
    loglyticsStore.getUnreadCount();
  }

  forgetMember() {
    this.member = undefined;
    this.accountId = undefined;
    this.appId = undefined;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async updateMyMemberDetails(updateInfo:any) {
    await memberConnection.updateMember(updateInfo);
    this.pullMember();
  }

  async markAnnouncementSeen(id: string) {
    const member = this.member!;
    if (member.seenAnnouncements?.includes(id)) return;
    const seenAnnouncements = [...(member.seenAnnouncements ?? []), id];
    member.seenAnnouncements = seenAnnouncements;
    try {
      await memberConnection.updateMember({seenAnnouncements});
    } catch (err) {
      log.e('failed saving seen announcements', err);
    }
  }

  updateAccount(account: IAccount) {
    for (let i = 0; i < this.member!.accounts.length; i++) {
      if (this.member!.accounts[i]._id === account._id) {
        this.member!.accounts[i] = account;
        break;
      }
    }
  }

  updateApp(app: IApp) {
    if (!this.account) return;
    for (let i = 0; i < this.account.apps.length; i++) {
      if (this.account.apps[i]._id === app._id) {
        this.account.apps[i] = app;
        this.setAppId(app._id);
        return;
      }
    }
  }

  addApp(app: IApp) {
    this.account!.apps.push(app);
    this.setAppId(app._id);
  }

  addAccount(account: IAccount) {

    if (!this.member!.accounts) this.member!.accounts = [account];
    else this.member!.accounts.push(account);
    this.accountId = account._id;
    this.getCurrentPlan();
  }

  deleteApp(appId: string) {
    if (!this.account) return;
    for (let i = 0; i < this.account.apps.length; i++) {
      if (this.account.apps[i]._id === appId) {
        this.account.apps.splice(i, 1);
        log.d('removed app', this.account.apps);
        return;
      }
    }
  }

  setAppId(appId?: string) {
    log.v('set current app:', appId);
    if (!appId) {
      this.appId = undefined;
      return;
    }
    if (this.appId === appId) return;
    for (const account of this.member!.accounts) {
      const app = account.apps.find((app) => (app._id === appId));
      if (app) {
        if (this.accountId !== account._id) {
          this.accountId = account._id;
          this.getCurrentPlan();
        }
        this.appId = appId;
        loglyticsStore.clear();
        loglyticsStore.getUnreadCount();
        alertsStore.getUnreadCount();
        log.d(`set current app - appId: ${appId} app name:${app.name} `)
        return;
      }
    }
    this.appId = undefined; // in case that It didn't find the app.
  }

  setAccountId(accountId: string) {
    log.v('set current account:', accountId);
    if (this.accountId === accountId) return;
    const account = this.member!.accounts.find(account => account._id === accountId);
    if (!account) { // doesn't have such account
      this.accountId = undefined;
      this.appId = undefined;
      return;
    }

    this.accountId = accountId;
    this.getCurrentPlan();
    // this.setAppId(account.apps[0]._id); The app id will be set by the app route that will call setAppId
  }

  async sendValidateEmail() {
    if (!this.member) return;
    await ConnectionService.sendValidateEmail(this.member._id);
  }

  async getReferrals() {
    if (!this.member) return;
    const referrals = await memberConnection.getReferrals();
    runInAction(() => this.referrals = referrals);
  }
}
export default new MemberStore();